package one.healthium.profile.ui.food_history

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import loco.FOOD_HISTORY_SCREEN_TITLE
import one.healthium.base.utils.Formatters.formatCalories
import one.healthium.loco.resolve
import one.healthium.profile.ui.components.SwipeToDismissContainer
import one.healthium.profile.ui.food_history.models.FoodItemUiModel
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppBackNavigationButton
import one.healthium.ui.compose.components.AppIcons
import one.healthium.ui.compose.components.AppLoadingScreen
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppSeparator
import one.healthium.ui.compose.components.AppTopBar
import one.healthium.ui.compose.components.adaptive.WindowSizeConstraintScrollableContainer
import one.healthium.ui.compose.theme.AppTheme
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

interface FoodHistoryEventDelegate {
    fun onBackClicked()
    fun onFoodItemClicked(foodItem: FoodItemUiModel)
}

@Composable
fun FoodHistoryScreen(eventDelegate: FoodHistoryEventDelegate) {
    val viewModel = koinViewModel<FoodHistoryViewModel>()
    val state by viewModel.state.collectAsState()

    FoodHistoryScreenContent(
        state = state,
        onBackClicked = eventDelegate::onBackClicked,
        onFoodItemClicked = eventDelegate::onFoodItemClicked,
        onFoodItemDeleteClicked = viewModel::onFoodItemDeleteClicked,
    )

    AlertUiMessageHandler(viewModel.uiMessages)
}

@Composable
private fun FoodHistoryScreenContent(
    state: FoodHistoryUiState,
    onBackClicked: () -> Unit,
    onFoodItemClicked: (FoodItemUiModel) -> Unit,
    onFoodItemDeleteClicked: (FoodItemUiModel) -> Unit,
) {
    AppScaffold(
        topBar = {
            AppTopBar(
                title = {
                    Text(stringResource(loco.Res.string.FOOD_HISTORY_SCREEN_TITLE))
                },
                navigationIcon = {
                    AppBackNavigationButton(onClick = onBackClicked)
                }
            )
        }
    ) {
        Box(modifier = Modifier.padding(it)) {
            FoodHistoryList(
                uiData = state.uiData,
                onFoodItemClicked = onFoodItemClicked,
                onFoodItemDeleteClicked = onFoodItemDeleteClicked,
            )

            AppLoadingScreen(visible = state.loading)
        }
    }
}

@Composable
private fun FoodHistoryList(
    modifier: Modifier = Modifier,
    uiData: List<Any>?,
    onFoodItemClicked: (FoodItemUiModel) -> Unit,
    onFoodItemDeleteClicked: (FoodItemUiModel) -> Unit,
) {
    if (uiData == null) return

    WindowSizeConstraintScrollableContainer(
        additionalContentPadding = PaddingValues(start = 12.dp, end = 12.dp, bottom = 80.dp),
    ) {
        LazyColumn(
            modifier = modifier,
            contentPadding = it,
        ) {
            items(
                count = uiData.size,
                contentType = { index -> uiData[index]::class },
                key = { index -> uiData[index].key() },
            ) { index ->
                Spacer(Modifier.height(6.dp))

                when (val item = uiData[index]) {
                    is FoodItemUiModel -> SwipeToDismissContainer(
                        modifier = Modifier.animateItem(),
                        onDelete = { onFoodItemDeleteClicked(item) }
                    ) {
                        FoodHistoryItem(
                            uiModel = item,
                            onClick = { onFoodItemClicked(item) },
                        )
                    }

                    is String -> AppSeparator(
                        modifier = Modifier.animateItem(),
                        text = item,
                    )
                }
            }
        }
    }
}

@Composable
fun FoodHistoryItem(
    uiModel: FoodItemUiModel,
    onClick: () -> Unit,
) {
    Row(
        modifier = Modifier
            .background(AppTheme.colors.surface, AppTheme.shapes.small)
            .clip(AppTheme.shapes.small)
            .clickable { onClick() }
            .padding(horizontal = 12.dp, vertical = 14.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            modifier = Modifier.weight(1f),
            text = uiModel.name,
            style = AppTheme.textStyles.secondary_14_400,
            color = AppTheme.colors.text100,
        )
        Spacer(Modifier.width(8.dp))
        Text(
            text = uiModel.calories.formatCalories().resolve(),
            style = AppTheme.textStyles.secondary_16_500,
            color = AppTheme.colors.text100,
        )
        Spacer(Modifier.width(8.dp))
        Image(
            modifier = Modifier.size(width = 8.dp, height = 13.dp),
            painter = painterResource(AppIcons.icArrowRight),
            contentDescription = null,
        )
    }
}

private fun Any.key(): Any = when (this) {
    is FoodItemUiModel -> this.id ?: this.name
    is String -> this
    else -> this
}